import React, { useState, useEffect } from 'react';
import { useRole, BACKEND_URL } from '../App';
import {
  GridSkeleton, EmptyState, getKpiStatus, PageHeader
} from '../components/shared';
import { toast } from 'sonner';
import { BarChart3, Pencil, Check, X, AlertCircle, TrendingUp, TrendingDown } from 'lucide-react';

export default function KPIDashboard() {
  const { role, canWrite } = useRole();
  const [kpis, setKpis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('weekly');
  const [editingKpi, setEditingKpi] = useState(null);
  const [editValue, setEditValue] = useState('');

  useEffect(() => {
    setLoading(true);
    fetch(`${BACKEND_URL}/api/kpis`)
      .then(r => r.json())
      .then(d => { setKpis(d.kpis || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleUpdate = async (kpiId) => {
    const numValue = parseFloat(editValue);
    if (isNaN(numValue)) { toast.error('Please enter a valid number'); return; }
    
    setKpis(prev => prev.map(k => k.id === kpiId ? { ...k, current_value: numValue } : k));
    setEditingKpi(null);
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/kpis/${kpiId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: numValue, role })
      });
      if (!res.ok) throw new Error();
      toast.success('KPI value updated');
    } catch {
      toast.error('Failed to update KPI');
    }
  };

  const tabs = role === 'Director'
    ? ['weekly', 'monthly', 'executive']
    : ['weekly', 'monthly'];

  const filteredKpis = activeTab === 'executive'
    ? kpis.filter(k => {
        const s = getKpiStatus(k.current_value, k.target);
        return s.color === 'orange' || s.color === 'red' || s.color === 'grey';
      })
    : kpis.filter(k => k.review_frequency?.toLowerCase() === activeTab);

  // Group by category for executive view
  const groupedKpis = {};
  if (activeTab === 'executive') {
    filteredKpis.forEach(k => {
      const cat = k.category || 'Other';
      if (!groupedKpis[cat]) groupedKpis[cat] = [];
      groupedKpis[cat].push(k);
    });
  }

  return (
    <div className="page-container" data-testid="kpi-dashboard">
      <PageHeader
        title="KPI Dashboard"
        subtitle="Track operational performance metrics"
      >
        {!canWrite && (
          <span className="text-xs px-3 py-1 rounded-full bg-muted text-muted-foreground" title="Only Director and Operations Manager can edit KPI values">
            View only
          </span>
        )}
      </PageHeader>

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-xl bg-muted mb-6 w-fit">
        {tabs.map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab
                ? 'bg-card shadow-sm text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid={`kpi-tab-${tab}`}
          >
            {tab === 'executive' ? 'Executive Summary' : tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {loading ? <GridSkeleton /> : (
        <>
          {activeTab === 'executive' ? (
            Object.keys(groupedKpis).length > 0 ? (
              Object.entries(groupedKpis).map(([category, categoryKpis]) => (
                <div key={category} className="mb-8">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">{category}</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {categoryKpis.map(kpi => <KPICard key={kpi.id} kpi={kpi} canWrite={canWrite} editingKpi={editingKpi} editValue={editValue} setEditingKpi={setEditingKpi} setEditValue={setEditValue} handleUpdate={handleUpdate} />)}
                  </div>
                </div>
              ))
            ) : (
              <EmptyState
                icon={TrendingUp}
                title="All KPIs on target"
                description="No KPIs require attention. All metrics are performing well."
              />
            )
          ) : (
            filteredKpis.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredKpis.map(kpi => <KPICard key={kpi.id} kpi={kpi} canWrite={canWrite} editingKpi={editingKpi} editValue={editValue} setEditingKpi={setEditingKpi} setEditValue={setEditValue} handleUpdate={handleUpdate} />)}
              </div>
            ) : (
              <EmptyState
                icon={BarChart3}
                title={`No ${activeTab} KPIs`}
                description={`No KPIs with ${activeTab} review frequency found.`}
              />
            )
          )}
        </>
      )}
    </div>
  );
}

function KPICard({ kpi, canWrite, editingKpi, editValue, setEditingKpi, setEditValue, handleUpdate }) {
  const status = getKpiStatus(kpi.current_value, kpi.target);
  const isEditing = editingKpi === kpi.id;

  const isCRM = kpi.source === 'crm';

  return (
    <div className={`ps-card-static p-5 ${status.cls}`} data-testid="kpi-card">
      <div className="flex items-start justify-between mb-1">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{kpi.category}</span>
          {isCRM && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold" style={{ background: 'hsl(185 40% 93%)', color: 'hsl(185 55% 30%)' }}>LIVE</span>
          )}
        </div>
        {canWrite && !isEditing && !isCRM && (
          <button
            onClick={() => { setEditingKpi(kpi.id); setEditValue(kpi.current_value ?? ''); }}
            className="p-1.5 rounded-lg hover:bg-muted/50 transition-colors"
            data-testid="kpi-inline-edit-button"
          >
            <Pencil size={14} className="text-muted-foreground" />
          </button>
        )}
      </div>
      <h3 className="text-sm font-semibold mb-4">{kpi.name}</h3>
      
      {isEditing ? (
        <div className="flex items-center gap-2 mb-3">
          <input
            type="number"
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            className="w-28 px-3 py-2 text-3xl font-bold rounded-lg border border-input bg-background"
            autoFocus
            data-testid="kpi-inline-edit-input"
            onKeyDown={e => { if (e.key === 'Enter') handleUpdate(kpi.id); if (e.key === 'Escape') setEditingKpi(null); }}
          />
          <button onClick={() => handleUpdate(kpi.id)} className="p-2 rounded-lg bg-primary text-primary-foreground" data-testid="kpi-inline-edit-save-button">
            <Check size={16} />
          </button>
          <button onClick={() => setEditingKpi(null)} className="p-2 rounded-lg hover:bg-muted" data-testid="kpi-inline-edit-cancel-button">
            <X size={16} />
          </button>
        </div>
      ) : (
        <div className="mb-3">
          <span className="text-4xl font-bold tracking-tight">
            {kpi.current_value !== null && kpi.current_value !== undefined ? kpi.current_value : '—'}
          </span>
          {kpi.metric_type && <span className="text-sm text-muted-foreground ml-1">{kpi.metric_type === 'Percentage' ? '%' : ''}</span>}
          <div className="text-sm text-muted-foreground mt-1">Target: {kpi.target ?? '—'}{kpi.metric_type === 'Percentage' ? '%' : ''}</div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full ${
          status.color === 'green' ? 'badge-active' :
          status.color === 'orange' ? 'badge-draft' :
          status.color === 'red' ? 'badge-critical' :
          'bg-muted text-muted-foreground'
        }`}>
          {status.color === 'green' && <TrendingUp size={12} />}
          {status.color === 'red' && <TrendingDown size={12} />}
          {status.color === 'orange' && <AlertCircle size={12} />}
          {status.label}
        </span>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>{kpi.review_frequency}</span>
        </div>
      </div>
      {kpi.owner_role && (
        <p className="text-xs text-muted-foreground mt-2">{kpi.owner_role}</p>
      )}
    </div>
  );
}
